import React, { useRef, useEffect } from "react";
import { observer } from "mobx-react-lite";
import {
  MapContainer,
  TileLayer,
  Popup,
  LayersControl,
  useMap,
  Circle,
  CircleMarker,
} from "react-leaflet";
import { Table, Typography, Button, Card, Row, Col, Badge, Tag } from "antd";
import { EnvironmentOutlined, RadarChartOutlined, WifiOutlined } from "@ant-design/icons";
import "leaflet/dist/leaflet.css";
import { useStore } from "../../stores/stores";

const { Title, Text } = Typography;
const { BaseLayer } = LayersControl;


const deviceLocations = [
  { name: "South Node",   serial_number: "7635010800025275", lat: 37.33796119565291, lng: 21.780084153963788 },
  { name: "PV-House-HN", serial_number: "7635010600026064", lat: 37.277697391487656, lng: 22.15739187360738  },
  { name: "Dexameni",    serial_number: "7635010900028319", lat: 36.7850,             lng: 22.4292            },
];

const FlyToLocation = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo([lat, lng], 18, { animate: true, duration: 1.5, easeLinearity: 0.25 });
  }, [lat, lng, map]);
  return null;
};

const HighlightMarker = ({ lat, lng }) => (
  <Circle center={[lat, lng]} radius={20} pathOptions={{ color: "red" }} />
);

const Home = () => {
  const { deviceStore } = useStore();
  const mapRef = useRef();
  const [flyTo, setFlyTo] = React.useState(null);

  useEffect(() => {
    deviceStore.fetchDevices();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tableData = deviceStore.devices.map(d => ({
    ...d,
    key: d.serial_number,
    lat: deviceLocations[d.name]?.[0],
    lng: deviceLocations[d.name]?.[1],
  }));

  const now = new Date();
  const dayDate = now.toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  const time = now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

  const columns = [
    {
      title: "Device",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <WifiOutlined style={{ color: "#52c41a" }} />
          <Text strong>{text}</Text>
        </span>
      ),
    },
    {
      title: "Serial Number",
      dataIndex: "serial_number",
      key: "serial_number",
      render: (text) => <Tag color="blue" style={{ fontFamily: "monospace" }}>{text}</Tag>,
    },
    {
      title: "Status",
      key: "status",
      render: () => <Badge status="processing" text="Online" />,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EnvironmentOutlined />}
          onClick={() => setFlyTo({ lat: record.lat, lng: record.lng, serial: record.serial_number, timestamp: Date.now() })}
        >
          View on Map
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: 24, background: '#f0f2f5', minHeight: '100%' }}>

      {/* Banner */}
      <div style={{
        background: "linear-gradient(135deg, #001529 0%, #003a70 100%)",
        borderRadius: 10,
        padding: "20px 28px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        color: "#fff",
      }}>
        <div>
          <Title level={2} style={{ color: "#fff", margin: 0 }}>CYBERNEMO Dashboard</Title>
          <Text style={{ color: "rgba(255,255,255,0.75)", fontSize: 14 }}>Smart Farming Pilot — Field Overview</Text>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#fff" }}>{time}</div>
          <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 13 }}>{dayDate}</div>
        </div>
      </div>

      {/* Stats */}
      <Row gutter={16}>
        <Col span={8}>
          <Card bordered={false} style={{ background: "#f6ffed", borderLeft: "4px solid #52c41a", borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <WifiOutlined style={{ fontSize: 28, color: "#52c41a" }} />
              <div>
                <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1 }}>{tableData.length}</div>
                <Text type="secondary">Active Devices</Text>
              </div>
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={false} style={{ background: "#e6f7ff", borderLeft: "4px solid #1890ff", borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <EnvironmentOutlined style={{ fontSize: 28, color: "#1890ff" }} />
              <div>
                <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1 }}>{tableData.length}</div>
                <Text type="secondary">Field Locations</Text>
              </div>
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={false} style={{ background: "#fff7e6", borderLeft: "4px solid #fa8c16", borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <RadarChartOutlined style={{ fontSize: 28, color: "#fa8c16" }} />
              <div>
                <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1 }}>Live</div>
                <Text type="secondary">Monitoring</Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Map */}
      <Card
        title={<span><EnvironmentOutlined style={{ marginRight: 8, color: "#1890ff" }} />Field Map</span>}
        bordered={false}
        style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
        styles={{ body: { padding: 0, overflow: "hidden", borderRadius: "0 0 8px 8px" } }}
      >
        <MapContainer
          center={[37.0, 22.0]}
          zoom={8}
          style={{ height: "480px", width: "100%" }}
          whenCreated={(mapInstance) => (mapRef.current = mapInstance)}
        >
          <LayersControl position="topright">
            <BaseLayer checked name="OpenStreetMap">
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
            </BaseLayer>
            <BaseLayer name="Satellite">
              <TileLayer url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}" attribution="&copy; Google" />
            </BaseLayer>
            <BaseLayer name="Terrain">
              <TileLayer url="https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}" attribution="&copy; Google" />
            </BaseLayer>
          </LayersControl>

          {deviceLocations.map((device) => (
            <CircleMarker
              key={device.serial_number}
              center={[device.lat, device.lng]}
              radius={10}
              pathOptions={{ color: '#1890ff', fillColor: '#1890ff', fillOpacity: 1, weight: 3 }}
            >
              <Popup>
                <b>{device.name}</b><br />SN: {device.serial_number}
              </Popup>
            </CircleMarker>
          ))}

          {flyTo && (
            <>
              <FlyToLocation lat={flyTo.lat} lng={flyTo.lng} />
              <HighlightMarker lat={flyTo.lat} lng={flyTo.lng} />
            </>
          )}
        </MapContainer>
      </Card>

      {/* Table */}
      <Card
        title={<span><WifiOutlined style={{ marginRight: 8, color: "#1890ff" }} />Registered Devices</span>}
        bordered={false}
        style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
      >
        <Table
          columns={columns}
          dataSource={tableData}
          pagination={false}
          size="middle"
        />
      </Card>

    </div>
  );
};

export default observer(Home);
